import { useCallback, useEffect, useRef, useState } from "react";

export type SaveStatus = "idle" | "pending" | "saving" | "saved" | "error";

type Options = {
  /** Debounce before an automatic save. */
  delay: number;
  /** When false, changes accumulate until `flush()` is called (e.g. published posts). */
  autoSave: boolean;
};

/**
 * Accumulates partial changes and saves them as one PATCH: debounced when `autoSave` is on,
 * or on demand via `flush()`. Changes queued while a save is in flight are kept for the next one,
 * and a failed save puts its changes back so nothing typed is lost.
 */
export const usePendingChanges = <T extends object>(
  save: (changes: Partial<T>) => Promise<void>,
  { delay, autoSave }: Options,
) => {
  const pending = useRef<Partial<T>>({});
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const inFlight = useRef<Promise<void> | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const [status, setStatus] = useState<SaveStatus>("idle");
  const [error, setError] = useState<unknown>(null);

  const hasPending = () => Object.keys(pending.current).length > 0;

  const flush = useCallback(async (): Promise<boolean> => {
    clearTimeout(timer.current);
    if (inFlight.current) await inFlight.current.catch(() => undefined);
    if (!hasPending()) return true;

    const changes = pending.current;
    pending.current = {};
    setStatus("saving");

    const attempt = saveRef.current(changes);
    inFlight.current = attempt;
    try {
      await attempt;
      setError(null);
      setStatus(hasPending() ? "pending" : "saved");
      return true;
    } catch (caught) {
      pending.current = { ...changes, ...pending.current };
      setError(caught);
      setStatus("error");
      return false;
    } finally {
      inFlight.current = null;
    }
  }, []);

  const queue = useCallback(
    (changes: Partial<T>) => {
      pending.current = { ...pending.current, ...changes };
      setStatus("pending");
      clearTimeout(timer.current);
      if (autoSave) timer.current = setTimeout(() => void flush(), delay);
    },
    [autoSave, delay, flush],
  );

  /** Drop queued changes (e.g. after a field-level conflict was handled). */
  const discard = useCallback((keys: (keyof T)[]) => {
    for (const key of keys) delete pending.current[key];
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  return { queue, flush, discard, status, error, hasPending };
};
